import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Space, Typography, Image } from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { apiClient, endpoints, unwrap } from '@core/api';
import { useAddToCart, useUpdateCartLine } from '@core/hooks/useCart';
import { flyToCart } from '@core/utils/flyToCart';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useToast } from '@core/hooks/useToast';
import { useEcommerceSettings } from '@core/settings';
import {
  getComboPositionQuantityCap,
  getComboGroupSelectedCount,
} from '@core/utils/comboSelection';
import {
  applyVariantSelection,
  buildVariantOptionStates,
  findSelectedSku,
  getValidSkus,
  isVariantSellable,
  selectionsForCartLine,
} from '@core/utils/variantSkus';
import { isSoldOutByOnlineInventory } from '@core/utils/productOptions';
import { useThemePlaceholderSrc } from '@core/utils/themePlaceholder';
import Price from '@components/base/Price';
import { useCopy } from '@/app/providers/useCopy';
import {
  formatAlternativeUnitConversion,
  getAlternativeUnitQuantity,
  getAlternativeUnitSelectionKey,
  getLineAlternativeUnitDetails,
  getSelectedAlternativeUomId,
  isSameAlternativeUnitConversion,
  normalizeAlternativeUnitServiceOption,
  resolveAlternativeUnitPrice,
  shouldHideAlternativeUnitName,
} from './options/units';
import {
  SKU_SERVICE_GROUP,
  asArray,
  buildItemUpsellGroups,
  buildModifierGroupList,
  firstNumber,
  firstVariantServiceMatch,
  getModifierSource,
  getSelectedItemUpsells,
  getVariantServiceItemEntries,
  getVariantServiceItemGroupList,
  getVariantServiceItemList,
  hasModifierCustomOptions,
  modifierSubtitle,
  normalizeComboGroup,
  normalizeIngredient,
  normalizeVariant,
  normalizeVariantGroup,
  variantDeclaresServiceItems,
  variantServiceLookupKeys,
} from './options/normalize';
import {
  buildComboPrefillState,
  buildIngredientPrefillKeys,
  buildModifierCustomPrefillState,
  buildPrefillState,
  buildServiceItemPrefillState,
  toPayloadGroupIdForSelection,
} from './options/prefill';
import {
  distinctCount,
  getModifierGroupMaxQuantity,
  hasQuantityMode,
  isSkuOptionBlocked,
  skuStateClass,
  sumQuantities,
  collectValidationErrors,
} from './options/rules';
import OptionPriceDelta from './options/components/OptionPriceDelta.jsx';
import FoodTypeMark from './options/components/FoodTypeMark.jsx';
import GroupBadge from './options/components/GroupBadge.jsx';
import QtyStepper from './options/components/QtyStepper.jsx';
import ModifierCustomChoices from './options/components/ModifierCustomChoices.jsx';
import UpsellChildren from './options/components/UpsellChildren.jsx';
import ServiceItemChildren from './options/components/ServiceItemChildren.jsx';
import ItemUpsellSection from './options/components/ItemUpsellSection.jsx';
import { buildCartLinePayload, buildItemUpsellLines } from './options/payload';
import { computeOptionPricing } from './options/totals';

const { Text } = Typography;

export default function ProductOptionView({ product, onDone, initialLine, onBack }) {
  const t = useCopy();
  const toast = useToast();
  const addToCart = useAddToCart();
  const updateCartLine = useUpdateCartLine();
  const { locationId, channelId } = useStoreContext();
  const placeholderImage = useThemePlaceholderSrc();

  const isVariantSku = isVariantSellable(product);
  const variantSkus = useMemo(() => product.variantSkus ?? [], [product.variantSkus]);

  const baseVariantGroups = useMemo(() => {
    const variantGroupSources = [
      ...asArray(product.variant_group_details),
      ...asArray(product.variant_groups),
    ];
    const serviceDataByGroupedVariantKey = new Map();

    for (const rawGroup of variantGroupSources) {
      const groupId = rawGroup?.variant_group_id ?? rawGroup?.group_id ?? rawGroup?.id ?? '';
      for (const rawOption of asArray(rawGroup?.variants ?? rawGroup?.items ?? rawGroup?.options)) {
        const serviceItems = getVariantServiceItemList(rawOption);
        const serviceItemGroups = getVariantServiceItemGroupList(rawOption);
        if (!serviceItems.length && !serviceItemGroups.length) continue;
        for (const key of variantServiceLookupKeys(groupId, rawOption)) {
          serviceDataByGroupedVariantKey.set(key, {
            serviceItems,
            serviceItemGroups,
          });
        }
      }
    }

    const groups = asArray(
      product.variant_group_details?.length
        ? product.variant_group_details
        : product.variant_groups,
    )
      .map(normalizeVariantGroup)
      .filter((group) => group.options.length > 0);
    const singleVariants = asArray(
      product.variants?.length
        ? product.variants
        : product.single_variant_details?.length
          ? product.single_variant_details
          : product.single_variants,
    ).map(normalizeVariant);
    const serviceItemsByVariantKey = new Map();
    const serviceItemGroupsByVariantKey = new Map();

    for (const variant of singleVariants) {
      const serviceItems = getVariantServiceItemList(variant);
      const serviceItemGroups = getVariantServiceItemGroupList(variant);
      if (!serviceItems.length && !serviceItemGroups.length) continue;
      for (const key of [variant.variant_id, variant.id, variant.name, variant.variant_name]) {
        if (!key) continue;
        if (serviceItems.length) serviceItemsByVariantKey.set(String(key), serviceItems);
        if (serviceItemGroups.length) {
          serviceItemGroupsByVariantKey.set(String(key), serviceItemGroups);
        }
      }
    }

    for (const group of groups) {
      const groupId = group.variant_group_id ?? (group.id === 'single-variants' ? '' : group.id);
      const groupHasOwnServiceItems = group.options.some(
        (option) =>
          variantDeclaresServiceItems(option) ||
          Boolean(firstVariantServiceMatch(serviceDataByGroupedVariantKey, groupId, option)),
      );

      group.options = group.options.map((option) => {
        const groupedServiceData = firstVariantServiceMatch(
          serviceDataByGroupedVariantKey,
          groupId,
          option,
        );
        const optionHasOwnServiceItems =
          variantDeclaresServiceItems(option) || Boolean(groupedServiceData);
        if (groupedServiceData && !variantDeclaresServiceItems(option)) {
          return {
            ...option,
            ...(groupedServiceData.serviceItems.length
              ? { service_items: groupedServiceData.serviceItems }
              : {}),
            ...(groupedServiceData.serviceItemGroups.length
              ? { service_item_groups: groupedServiceData.serviceItemGroups }
              : {}),
            __hasOwnServiceItems: true,
          };
        }

        if (groupHasOwnServiceItems && !optionHasOwnServiceItems) {
          return {
            ...option,
            service_items: [],
            service_item_groups: [],
            serviceItems: [],
            serviceItemGroups: [],
            __hasOwnServiceItems: false,
          };
        }

        const optionKeys = [option.variant_id, option.id, option.name, option.variant_name].map(
          (key) => String(key ?? ''),
        );
        const inheritedServiceItems =
          groupHasOwnServiceItems || optionHasOwnServiceItems
            ? null
            : optionKeys.map((key) => serviceItemsByVariantKey.get(key)).find(Boolean);
        const inheritedServiceItemGroups = getVariantServiceItemGroupList(option).length
          ? null
          : groupHasOwnServiceItems
            ? null
            : optionKeys.map((key) => serviceItemGroupsByVariantKey.get(key)).find(Boolean);
        if (!inheritedServiceItems && !inheritedServiceItemGroups) return option;
        return {
          ...option,
          ...(inheritedServiceItems ? { service_items: inheritedServiceItems } : {}),
          ...(inheritedServiceItemGroups
            ? { service_item_groups: inheritedServiceItemGroups }
            : {}),
          __hasOwnServiceItems: true,
        };
      });
    }

    if (singleVariants.length) {
      groups.push({
        id: 'single-variants',
        name: t('variants', 'Variants'),
        required: false,
        options: singleVariants,
      });
    }

    return groups;
  }, [product, t]);

  // Variant-sellable items list the ungrouped single variants too: a SKU can
  // be a single variant on its own (e.g. "Thin Crust") or part of a mixed
  // combination, and picking it resolves that SKU like any grouped variant.
  const variantGroups = baseVariantGroups;

  const comboGroups = useMemo(() => {
    return asArray(product.combo_groups ?? product.comboGroups)
      .map((group, index) => normalizeComboGroup(group, index, t))
      .filter((group) => group.positions.length > 0);
  }, [product, t]);

  // Item upsells become separate cart lines, so they are only offered when
  // adding; editing an existing line never re-adds them.
  const isEditingLine = Boolean(initialLine?.lineId);
  const itemUpsellGroups = useMemo(
    () => (isEditingLine ? [] : buildItemUpsellGroups(product, t)),
    [isEditingLine, product, t],
  );

  // Visibility of the "remove ingredient" section is opt-in per account/location —
  // read from the same runtime settings store HomePage already reads for
  // top_selling_items/top_selling_categories. Flag name is inferred (not yet
  // confirmed against a live payload); missing/unset defaults to shown.
  const { displayIngredients: showIngredients } = useEcommerceSettings();

  const sellingAlternativeUnits = useMemo(() => {
    return (product.alternative_units_and_conversions || []).filter((conv) =>
      conv.use_for?.includes('SELLING'),
    );
  }, [product.alternative_units_and_conversions]);

  // Presentation model for the pack-size cards. Conversion/label maths is reused
  // from the existing helpers so only the layout changes here, not the numbers.
  const packOptions = useMemo(() => {
    const basePrice = firstNumber(product.price, 0) ?? 0;

    const packs = sellingAlternativeUnits.map((conv, index) => {
      const rate = firstNumber(conv.conversion_rate, conv.conversionRate, 1) ?? 1;
      const formatted = formatAlternativeUnitConversion(conv, rate);
      const hideName = shouldHideAlternativeUnitName(conv);
      const altName = conv.unit_details?.name || conv.unit || 'Unit';
      const measure = `${formatted.quantity} ${formatted.unitName}`;
      const serviceOption = normalizeAlternativeUnitServiceOption(conv, index);

      return {
        key: getAlternativeUnitSelectionKey(conv, index),
        conversion: conv,
        serviceOption,
        label: hideName ? measure : `${altName} · ${measure}`,
        price: resolveAlternativeUnitPrice(conv, basePrice),
        quantity: getAlternativeUnitQuantity(conv, rate),
        unitName: formatted.unitName,
        barcode: conv.barcode,
      };
    });

    // The rate line only makes sense when every pack is measured in the same
    // unit — otherwise "per 100 g" would be compared against "per piece".
    const unitNames = new Set(packs.map((pack) => pack.unitName));
    const comparable =
      packs.length > 1 && unitNames.size === 1 && packs.every((pack) => pack.quantity > 0);

    if (!comparable) return packs;

    const rateBase = Math.min(...packs.map((pack) => pack.quantity));
    const withRates = packs.map((pack) => ({
      ...pack,
      rateBase,
      rateAmount: Math.round((pack.price / pack.quantity) * rateBase),
      unitRate: pack.price / pack.quantity,
    }));

    const cheapest = Math.min(...withRates.map((pack) => pack.unitRate));
    const dearest = Math.max(...withRates.map((pack) => pack.unitRate));

    // When every pack costs the same per unit there is no best value and the rate
    // line would just repeat one number on every card, so drop both.
    if (cheapest === dearest) return packs;

    return withRates.map((pack) => ({ ...pack, isBestValue: pack.unitRate === cheapest }));
  }, [sellingAlternativeUnits, product.price]);

  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [variantSelections, setVariantSelections] = useState({});
  const [modifierSelections, setModifierSelections] = useState({});
  const [upsellSelections, setUpsellSelections] = useState({});
  // { [upsellGroupId]: { [upsellItemId]: count } }
  const [itemUpsellSelections, setItemUpsellSelections] = useState({});
  const [serviceItemSelections, setServiceItemSelections] = useState({});
  const [modifierCustomSelections, setModifierCustomSelections] = useState({});
  const [selectedAltUnitKey, setSelectedAltUnitKey] = useState(null);
  // comboPositionState: { [positionId]: { quantity: number, swapKey: string|null } }
  const [comboPositionState, setComboPositionState] = useState({});
  // Nested modifier picks on a resolved combo component, keyed by
  // `${positionId}::${swapKey || 'default'}` so swapping never cross-contaminates
  // a previously-cached selection for a different component on the same slot.
  const [comboComponentModifierSelections, setComboComponentModifierSelections] = useState({});

  // sellable_level === 'variant' only: the chosen combination's SKU.
  const selectedSku = useMemo(
    () => (isVariantSku ? findSelectedSku(variantGroups, variantSkus, variantSelections) : null),
    [isVariantSku, variantGroups, variantSkus, variantSelections],
  );
  // Variant-sellable items take their modifiers from the selected SKU when it
  // carries its own; every rule (single/multi, min/max, quantity caps,
  // alternates, extras) then applies to them exactly as to item modifiers.
  const modifierSourceKey =
    isVariantSku && selectedSku?.hasOwnModifiers ? `sku:${selectedSku.id}` : 'item';
  const modifierGroups = useMemo(
    () =>
      buildModifierGroupList(
        getModifierSource(product, isVariantSku ? selectedSku : null),
        { locationId, channelId },
        t,
      ),
    // selectedSku is read through modifierSourceKey so a new combination with
    // the same (item-level) modifiers keeps the groups — and the picks — stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channelId, locationId, product, t, modifierSourceKey],
  );
  // Set of dedup keys (ingredient id/name) currently marked "remove".
  const [removedIngredientKeys, setRemovedIngredientKeys] = useState(() => new Set());
  // Map of variant_id -> fetched recipe rules ingredients (filtered action is 'set' or 'add')
  const [variantRecipeIngredientsMap, setVariantRecipeIngredientsMap] = useState({});
  const fetchedVariantIdsRef = useRef(new Set());

  const fetchRecipeRulesForVariant = useCallback(
    async (variantId) => {
      if (!variantId || fetchedVariantIdsRef.current.has(variantId)) return;
      fetchedVariantIdsRef.current.add(variantId);

      const itemId = product.id || product.item_id;
      const recipeId =
        product.recipe_id ||
        (Array.isArray(product.recipe_details)
          ? product.recipe_details[0]?.recipe_id
          : product.recipe_details?.recipe_id);
      if (!itemId || !recipeId) return;

      const url = endpoints.catalog.recipeRulesItemPreview(itemId, recipeId);

      try {
        const raw = await apiClient.get(url, {
          query: { variant_id: variantId },
        });
        const { data } = unwrap(raw);
        const list =
          data?.recipe?.ingredients || raw?.recipe?.ingredients || data?.ingredients || [];
        const validIngredients = list.filter((i) => !i?.action || /^(set|add)/i.test(i.action));

        setVariantRecipeIngredientsMap((prev) => ({ ...prev, [variantId]: validIngredients }));
      } catch (err) {
        fetchedVariantIdsRef.current.delete(variantId);
        console.warn('Failed to fetch recipe rules for variant:', variantId, err);
      }
    },
    [product.id, product.item_id, product.recipe_id, product.recipe_details],
  );

  useEffect(() => {
    const modifierOpts = { locationId, channelId };
    setItemUpsellSelections({});
    if (initialLine?.lineId) {
      setQuantity(initialLine.quantity ?? 1);
      // Variant-sellable lines: rebuild the combination first, then read the
      // modifier picks against that SKU's modifier groups.
      const skuSelections = isVariantSku
        ? (selectionsForCartLine(variantGroups, variantSkus, initialLine) ?? {})
        : null;
      const lineSku = isVariantSku
        ? findSelectedSku(variantGroups, variantSkus, skuSelections)
        : null;
      const lineModifierGroups = buildModifierGroupList(
        getModifierSource(product, lineSku),
        modifierOpts,
        t,
      );
      const nextState = buildPrefillState(initialLine, variantGroups, lineModifierGroups);
      setVariantSelections(isVariantSku ? skuSelections : nextState.variantSelections);
      setModifierSelections(nextState.modifierSelections);
      setUpsellSelections(initialLine.upsellSelections ?? {});
      setServiceItemSelections(buildServiceItemPrefillState(initialLine));
      setModifierCustomSelections(buildModifierCustomPrefillState(initialLine, lineModifierGroups));
      const selectedAltDetails = getLineAlternativeUnitDetails(initialLine);
      const selectedIndex = sellingAlternativeUnits.findIndex((conv) =>
        isSameAlternativeUnitConversion(conv, selectedAltDetails, initialLine),
      );
      setSelectedAltUnitKey(
        selectedIndex >= 0
          ? getAlternativeUnitSelectionKey(sellingAlternativeUnits[selectedIndex], selectedIndex)
          : null,
      );
      const comboPrefill = buildComboPrefillState(initialLine, comboGroups);
      setComboPositionState(comboPrefill.positionState);
      setComboComponentModifierSelections(comboPrefill.componentModifierSelections);
      setRemovedIngredientKeys(buildIngredientPrefillKeys(initialLine));
      return;
    }

    setQuantity(1);

    setVariantSelections({});

    setModifierSelections({});
    setUpsellSelections({});
    setServiceItemSelections({});
    setModifierCustomSelections({});
    setSelectedAltUnitKey(null);
    // Combo/ingredient selections never carry over onto a brand-new line, even
    // when another cart line for this same product already has picks — unlike
    // variant/modifier defaults above, these always start blank.
    setComboPositionState({});
    setComboComponentModifierSelections({});
    setRemovedIngredientKeys(new Set());
  }, [
    initialLine,
    variantGroups,
    comboGroups,
    product,
    locationId,
    channelId,
    t,
    sellingAlternativeUnits,
    isVariantSku,
    variantSkus,
  ]);

  const toggleUpsellItem = (groupId, item) =>
    setUpsellSelections((prev) => ({
      ...prev,
      [groupId]: prev[groupId]?.id === item.id ? null : item,
    }));

  // Pick-one groups keep a single item (any count); multi groups keep many.
  const setItemUpsellQty = (group, item, qty) =>
    setItemUpsellSelections((prev) => {
      const next = group.isMultiSelect ? { ...prev[group.id] } : {};
      if (qty > 0) next[item.id] = qty;
      else delete next[item.id];
      return { ...prev, [group.id]: next };
    });

  const selectedItemUpsells = useMemo(
    () => getSelectedItemUpsells(itemUpsellGroups, itemUpsellSelections),
    [itemUpsellGroups, itemUpsellSelections],
  );
  const itemUpsellTotal = selectedItemUpsells.reduce((sum, u) => sum + u.price * u.quantity, 0);

  const toggleServiceItem = (key, groupScope = '') =>
    setServiceItemSelections((prev) => {
      if (!groupScope) {
        return {
          ...prev,
          [key]: !prev[key],
        };
      }

      const next = { ...prev };
      for (const selectedKey of Object.keys(next)) {
        if (selectedKey.startsWith(groupScope)) delete next[selectedKey];
      }
      if (!prev[key]) next[key] = true;
      return next;
    });

  const setModifierAlternate = (parentKey, option) => {
    setModifierCustomSelections((prev) => {
      const current = prev[parentKey] || { alternate: null, extras: {} };
      const isSelected = current.alternate?.id === option.id;
      return {
        ...prev,
        [parentKey]: {
          alternate: isSelected ? null : { ...option, quantity: 1 },
          extras: {},
        },
      };
    });
  };

  const toggleModifierExtra = (parentKey, group, item) => {
    setModifierCustomSelections((prev) => {
      const current = prev[parentKey] || { alternate: null, extras: {} };
      const extras = { ...(current.extras || {}) };
      if (extras[group.id]?.id === item.id) {
        delete extras[group.id];
      } else {
        extras[group.id] = { ...item, quantity: item.quantity ?? 1 };
      }

      return {
        ...prev,
        [parentKey]: {
          alternate: null,
          extras,
        },
      };
    });
  };

  const { upsellDelta, selectedUpsellPayload } = useMemo(() => {
    const list = Object.values(upsellSelections).filter(Boolean);
    return {
      upsellDelta: list.reduce((sum, item) => sum + (item.price || 0), 0),
      selectedUpsellPayload: list.map((entry) => ({
        modifier_id: entry.modifier_id ?? entry.upsell_item_id ?? entry.id,
        name: entry.name,
        quantity: entry.quantity ?? 1,
        upsell_group_id: entry.upsell_group_id ?? entry.upselling_group_id ?? '',
        upsell_item_id: entry.upsell_item_id ?? entry.id,
        variant_group_id: entry.variant_group_id ?? '',
        variant_id: entry.variant_id ?? '',
      })),
    };
  }, [upsellSelections]);

  const { modifierCustomDelta, replacedModifierKeys, selectedModifierCustomPayload } =
    useMemo(() => {
      const rows = [];
      const replacedKeys = new Set();
      let delta = 0;

      for (const [parentKey, selection] of Object.entries(modifierCustomSelections)) {
        const [groupId, optionId] = parentKey.split('::');
        const parentQuantity = modifierSelections[groupId]?.[optionId] ?? 0;
        if (parentQuantity <= 0) continue;

        const parentGroup = modifierGroups.find((group) => group.id === groupId);
        const parent = parentGroup?.options.find((option) => option.id === optionId);
        const parentModifierId = parent?.modifier_id ?? parent?.id ?? optionId;

        if (selection?.alternate) {
          const alternate = selection.alternate;
          const quantity = parentQuantity;
          replacedKeys.add(parentKey);
          delta += (alternate.priceDelta || 0) * quantity;
          rows.push({
            modifier_group_id: groupId,
            modifier_group_name: parentGroup?.name ?? '',
            modifier_id: alternate.modifier_id ?? alternate.id,
            name: alternate.name ?? alternate.modifier_name,
            price_delta: alternate.priceDelta,
            quantity,
            replaced_modifier_id: parentModifierId,
            replaced_name: parent?.name ?? parent?.modifier_name,
          });
        }

        for (const extra of Object.values(selection?.extras || {}).filter(Boolean)) {
          const quantity = extra.quantity ?? 1;
          delta += (extra.price || 0) * quantity;
          rows.push({
            modifier_id: extra.modifier_id ?? extra.upsell_item_id ?? extra.id,
            parent_modifier_id: parentModifierId,
            upsell_item_id: extra.upsell_item_id ?? extra.id,
            upsell_group_id: extra.upsell_group_id ?? '',
            upsell_group_name: extra.upsell_group_name ?? '',
            is_extra: true,
            name: extra.name,
            quantity,
            price_delta: extra.price,
          });
        }
      }

      return {
        modifierCustomDelta: delta,
        replacedModifierKeys: replacedKeys,
        selectedModifierCustomPayload: rows,
      };
    }, [modifierCustomSelections, modifierGroups, modifierSelections]);

  const selectedVariants = variantGroups
    .map((group) => {
      const option = group.options.find((item) => item.id === variantSelections[group.id]);
      return option
        ? {
            ...option,
            groupId: group.id,
            groupName: group.name,
          }
        : null;
    })
    .filter(Boolean);

  // A new combination starts its gallery from its first image.
  useEffect(() => {
    setActiveImageIndex(0);
  }, [selectedSku?.id]);

  // sellable_level === 'variant' only: enabled / faded / out-of-stock state of
  // every option.
  const variantOptionStates = useMemo(
    () =>
      isVariantSku ? buildVariantOptionStates(variantGroups, variantSkus, variantSelections) : null,
    [isVariantSku, variantGroups, variantSkus, variantSelections],
  );
  const hasValidSkus = isVariantSku && getValidSkus(variantSkus).length > 0;
  const skuServiceOption = useMemo(
    () =>
      selectedSku
        ? {
            id: selectedSku.id,
            variant_id: selectedSku.id,
            service_items: selectedSku.serviceItems,
            service_item_groups: selectedSku.serviceItemGroups,
            __hasOwnServiceItems:
              selectedSku.serviceItems.length > 0 || selectedSku.serviceItemGroups.length > 0,
          }
        : null,
    [selectedSku],
  );

  // Selections in variant-SKU mode can change programmatically (conflicting
  // groups get cleared, edit prefill), so load recipe rules from here.
  useEffect(() => {
    if (!isVariantSku) return;
    for (const group of variantGroups) {
      const option = group.options.find((item) => item.id === variantSelections[group.id]);
      const variantId = option?.variant_id || option?.id;
      if (variantId) fetchRecipeRulesForVariant(variantId);
    }
  }, [isVariantSku, variantGroups, variantSelections, fetchRecipeRulesForVariant]);

  const selectedModifiers = modifierGroups.flatMap((group) =>
    group.options
      .map((option) => ({
        ...option,
        groupId: group.id,
        groupName: group.name,
        quantity: modifierSelections[group.id]?.[option.id] ?? 0,
      }))
      .filter(
        (option) => option.quantity > 0 && !replacedModifierKeys.has(`${group.id}::${option.id}`),
      ),
  );

  // Variant-SKU mode: service items come only from the selected SKU, keyed
  // under the SKU_SERVICE_GROUP scope; each one goes out with quantity 1 and
  // the group it was picked under.
  const selectedSkuServiceItems = skuServiceOption
    ? getVariantServiceItemEntries(skuServiceOption, { locationId, channelId })
        .filter(({ item, serviceItemGroupId }) => {
          const key = `${SKU_SERVICE_GROUP.id}::${skuServiceOption.id}::${serviceItemGroupId || ''}::${item.service_item_id}`;
          return !item.isSoldOut && (item.isMandatory || serviceItemSelections[key]);
        })
        .map(({ item, serviceItemGroupId }) => ({
          service_item_id: item.service_item_id,
          name: item.name,
          price_delta: item.priceDelta,
          quantity: 1,
          ...(serviceItemGroupId || item.service_item_group_id
            ? { service_item_group_id: serviceItemGroupId || item.service_item_group_id }
            : {}),
        }))
    : [];

  const selectedServiceItems = selectedVariants.flatMap((variant) => {
    if (isVariantSku) return [];
    if (!variantDeclaresServiceItems(variant)) return [];

    const variantGroupId = toPayloadGroupIdForSelection(variant.groupId);
    const variantId = variant.variant_id ?? variant.id;
    return getVariantServiceItemEntries(variant, { locationId, channelId })
      .filter(({ item, serviceItemGroupId }) => {
        const key = `${variantGroupId || 'single-variants'}::${variantId}::${serviceItemGroupId || ''}::${item.service_item_id}`;
        return !item.isSoldOut && (item.isMandatory || serviceItemSelections[key]);
      })
      .map(({ item, serviceItemGroupId }) => ({
        service_item_id: item.service_item_id,
        name: item.name,
        price_delta: item.priceDelta,
        ...(serviceItemGroupId ? { service_item_group_id: serviceItemGroupId } : {}),
        ...(variantGroupId ? { variant_group_id: variantGroupId } : {}),
        variant_id: variantId,
      }));
  });
  const selectedAltPack = packOptions.find((pack) => pack.key === selectedAltUnitKey);
  const selectedAlternativeUnitServiceItems =
    selectedAltPack && variantDeclaresServiceItems(selectedAltPack.serviceOption)
      ? getVariantServiceItemEntries(selectedAltPack.serviceOption, { locationId, channelId })
          .filter(({ item, serviceItemGroupId }) => {
            const key = `alternative-unit::${selectedAltPack.serviceOption.id}::${serviceItemGroupId || ''}::${item.service_item_id}`;
            return !item.isSoldOut && (item.isMandatory || serviceItemSelections[key]);
          })
          .map(({ item, serviceItemGroupId }) => ({
            service_item_id: item.service_item_id,
            name: item.name,
            price_delta: item.priceDelta,
            ...(serviceItemGroupId ? { service_item_group_id: serviceItemGroupId } : {}),
            alternative_unit: getSelectedAlternativeUomId(selectedAltPack.conversion),
            conversion_rate:
              firstNumber(
                selectedAltPack.conversion?.conversion_rate,
                selectedAltPack.conversion?.conversionRate,
              ) ?? undefined,
          }))
      : [];
  const selectedPayloadServiceItems = isVariantSku
    ? selectedSkuServiceItems
    : [...selectedServiceItems, ...selectedAlternativeUnitServiceItems];

  const productRecipe = useMemo(() => {
    if (Array.isArray(product.recipe) && product.recipe.length > 0) return product.recipe;
    if (Array.isArray(product.ingredients) && product.ingredients.length > 0)
      return product.ingredients;
    if (Array.isArray(product.recipe_details)) {
      return product.recipe_details.flatMap((r) =>
        Array.isArray(r?.ingredients) ? r.ingredients : r?.ingredient_name ? [r] : [],
      );
    }
    if (product.recipe_details && typeof product.recipe_details === 'object') {
      if (Array.isArray(product.recipe_details.ingredients))
        return product.recipe_details.ingredients;
      if (product.recipe_details.ingredient_name || product.recipe_details.ingredient_id)
        return [product.recipe_details];
    }
    return [];
  }, [product.recipe, product.ingredients, product.recipe_details]);

  // Ingredient list: base recipe when no variant is selected, otherwise the
  // union of every currently-selected variant's own recipe (falling back to
  // the base recipe for a selected variant that doesn't define its own —
  // the spec doesn't pin this edge case down, flagged for backend review).
  const ingredientEntries = useMemo(() => {
    const scope = selectedVariants.length > 0 ? selectedVariants : [{ id: '', groupId: '' }];
    return scope.flatMap((variant) => {
      const vId = variant.variant_id ?? variant.id;
      const fetchedList = variantRecipeIngredientsMap[vId];
      const rawRecipe =
        fetchedList ??
        product.recipeVariantIngredients?.[vId] ??
        variant.recipe ??
        variant.ingredients;
      const list = Array.isArray(rawRecipe) ? rawRecipe : asArray(productRecipe);
      return list
        .map((raw, index) => ({
          ingredient: normalizeIngredient(raw, index),
          variantId: variant.id,
          variantGroupId: variant.groupId === 'single-variants' ? '' : variant.groupId,
        }))
        .filter((entry) => fetchedList || entry.ingredient.isExcludable);
    });
  }, [
    selectedVariants,
    productRecipe,
    variantRecipeIngredientsMap,
    product.recipeVariantIngredients,
  ]);

  // Same ingredient appearing in >1 selected variant collapses to a single row
  // (no duplicate row, no per-variant subtitle) while every variant it belongs
  // to is tracked underneath — needed to expand back into one payload entry
  // per variant on add.
  const dedupedIngredients = useMemo(() => {
    const map = new Map();
    for (const entry of ingredientEntries) {
      const key = entry.ingredient.id;
      if (!map.has(key)) {
        map.set(key, { id: key, name: entry.ingredient.name, sources: [] });
      }
      map
        .get(key)
        .sources.push({ variantId: entry.variantId, variantGroupId: entry.variantGroupId });
    }
    return Array.from(map.values());
  }, [ingredientEntries]);

  const toggleIngredient = (key) => {
    setRemovedIngredientKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removedIngredientsPayload = dedupedIngredients
    .filter((ingredient) => removedIngredientKeys.has(ingredient.id))
    .flatMap((ingredient) =>
      ingredient.sources.map((source) => ({
        ingredient_id: ingredient.id,
        name: ingredient.name,
        variant_id: source.variantId || '',
        ...(source.variantGroupId ? { variant_group_id: source.variantGroupId } : {}),
      })),
    );

  // Combo groups: resolve each position's active component (default item or
  // swapped alternative), its price, and its nested modifier state.
  const comboPositionInfo = useMemo(() => {
    const map = {};
    for (const group of comboGroups) {
      for (const position of group.positions) {
        const state = comboPositionState[position.id];
        const quantity = state?.quantity ?? 0;
        const swapKey = state?.swapKey ?? null;
        const chip = swapKey ? (position.chips.find((c) => c.key === swapKey) ?? null) : null;
        const componentKey = `${position.id}::${swapKey || 'default'}`;
        const componentDetail = chip ? chip.detail : position.detail;
        const componentPrice = chip ? chip.priceDelta : position.price;
        map[position.id] = {
          group,
          position,
          quantity,
          swapKey,
          chip,
          componentKey,
          componentDetail,
          componentPrice,
        };
      }
    }
    return map;
  }, [comboGroups, comboPositionState]);

  const setComboComponentModifierQuantity = (componentKey, group, option, nextQty) => {
    setComboComponentModifierSelections((current) => {
      const componentSelection = current[componentKey] || {};
      const groupSelection = { ...(componentSelection[group.id] || {}) };
      const maxAllowedQty = getModifierGroupMaxQuantity(group, option, groupSelection);
      const cappedQty =
        maxAllowedQty > 0 ? Math.min(maxAllowedQty, Math.max(0, nextQty)) : Math.max(0, nextQty);

      if (group.selectionType === 'SINGLE' && cappedQty > 0) {
        const cleared = { [option.id]: cappedQty };
        return { ...current, [componentKey]: { ...componentSelection, [group.id]: cleared } };
      }

      if (cappedQty <= 0) {
        delete groupSelection[option.id];
      } else {
        groupSelection[option.id] = cappedQty;
      }

      return { ...current, [componentKey]: { ...componentSelection, [group.id]: groupSelection } };
    });
  };

  // Combo pricing = each SELECTED position's own component price + its nested
  // variant/modifier deltas, times that position's own picked quantity — added
  // once per line, never re-multiplied by overall item quantity (mirrors the
  // modifierDelta treatment below).
  const comboDelta = Object.values(comboPositionInfo).reduce((total, info) => {
    if (info.quantity <= 0) return total;
    const nestedSelection = comboComponentModifierSelections[info.componentKey] || {};
    const nestedDelta = asArray(info.componentDetail?.modifierGroups).reduce((sum, group) => {
      return (
        sum +
        group.options.reduce(
          (s, option) =>
            s + (option.priceDelta || 0) * (nestedSelection[group.id]?.[option.id] || 0),
          0,
        )
      );
    }, 0);
    return total + (info.componentPrice + nestedDelta) * info.quantity;
  }, 0);

  const comboSelectionsPayload = Object.values(comboPositionInfo)
    .filter((info) => info.quantity > 0)
    .map((info) => {
      const nestedSelection = comboComponentModifierSelections[info.componentKey] || {};
      const nestedModifiers = asArray(info.componentDetail?.modifierGroups).flatMap((group) =>
        group.options
          .filter((option) => (nestedSelection[group.id]?.[option.id] ?? 0) > 0)
          .map((option) => ({
            modifier_id: option.modifier_id ?? option.id,
            name: option.name ?? option.modifier_name,
            modifier_group_id: group.id,
            modifier_group_name: group.name,
            quantity: nestedSelection[group.id][option.id],
            price_delta: option.priceDelta,
          })),
      );
      const nestedModifierDelta = nestedModifiers.reduce(
        (sum, mod) => sum + (mod.price_delta || 0) * mod.quantity,
        0,
      );
      const chosenItemId = info.chip ? info.chip.alternativeItemId : info.position.itemId;
      const chosenName = info.chip ? info.chip.name : info.position.name;

      return {
        group_id: info.group.id,
        group_name: info.group.name,
        item_id: chosenItemId,
        item_name: chosenName,
        quantity: info.quantity,
        price_delta: info.componentPrice + nestedModifierDelta,
        ...(info.chip ? { parent_item_id: info.position.itemId } : {}),
        ...(nestedModifiers.length ? { modifiers: nestedModifiers } : {}),
        ...(info.chip?.variantId
          ? {
              variants: [
                {
                  variant_id: info.chip.variantId,
                  variant_name: info.chip.variantName,
                  ...(info.chip.variantGroupId
                    ? { variant_group_id: info.chip.variantGroupId }
                    : {}),
                },
              ],
            }
          : {}),
      };
    });

  const toggleComboPosition = (group, position) => {
    setComboPositionState((current) => {
      const existing = current[position.id];
      const isSelected = (existing?.quantity ?? 0) > 0;

      if (!group.isMultiSelect) {
        const next = { ...current };
        for (const sibling of group.positions) delete next[sibling.id];
        if (isSelected) return next; // tap off -> deselect, swap reset by dropping state
        next[position.id] = { quantity: 1, swapKey: null };
        return next;
      }

      // Multi-select: tapping an already-selected row must never change
      // quantity — only the +/- buttons do. Tapping an untouched row starts
      // it at qty 1.
      if (isSelected) return current;
      return { ...current, [position.id]: { quantity: 1, swapKey: null } };
    });
  };

  const setComboPositionQuantity = (group, position, nextQty) => {
    setComboPositionState((current) => {
      const cap = getComboPositionQuantityCap(group, position, current);
      const capped = Number.isFinite(cap)
        ? Math.min(cap, Math.max(0, nextQty))
        : Math.max(0, nextQty);
      const next = { ...current };
      if (capped <= 0) {
        delete next[position.id]; // qty -> 0 also resets any swap
      } else {
        next[position.id] = { quantity: capped, swapKey: current[position.id]?.swapKey ?? null };
      }
      return next;
    });
  };

  const selectComboChip = (group, position, chip) => {
    setComboPositionState((current) => {
      const existing = current[position.id];
      const alreadyActive = existing?.swapKey === chip.key && (existing?.quantity ?? 0) > 0;

      if (alreadyActive) {
        // Tapping the active chip again fully deselects the whole position.
        const next = { ...current };
        delete next[position.id];
        return next;
      }

      if (!group.isMultiSelect) {
        const next = { ...current };
        for (const sibling of group.positions) delete next[sibling.id];
        next[position.id] = { quantity: 1, swapKey: chip.key };
        return next;
      }

      return {
        ...current,
        [position.id]: {
          quantity: existing?.quantity > 0 ? existing.quantity : 1,
          swapKey: chip.key,
        },
      };
    });
  };

  // In variant-SKU mode the SKU's location/channel-resolved selling_price is
  // the final unit price, so per-variant deltas are not added on top. Until a
  // full combination is picked there is no price to show — the item's own
  // price is not a sellable figure.
  const selectedAltConv = sellingAlternativeUnits.find(
    (conv, idx) => getAlternativeUnitSelectionKey(conv, idx) === selectedAltUnitKey,
  );
  const pricing = computeOptionPricing({
    product,
    isVariantSku,
    selectedSku,
    selectedVariants,
    selectedModifiers,
    selectedServiceItems: selectedPayloadServiceItems,
    alternativeUnit: selectedAltConv,
    quantity,
    modifierCustomDelta,
    comboDelta,
    upsellDelta,
  });
  const { isPricePending, baseUnitPrice, lineTotal, displayPrice, displayMrp } = pricing;
  // Variant-SKU mode ignores item-level stock: out of stock only when the
  // selected SKU is, or when no valid combination exists at all.
  const isOutOfStock = isVariantSku
    ? !hasValidSkus || Boolean(selectedSku?.isOutOfStock)
    : Boolean(product.isOutOfStock ?? !product.isAvailable);
  const displayImageUrl = (isVariantSku && selectedSku?.images[0]) || product.imageUrl;
  // Selected combination's gallery: the active image fills the media area and
  // the remaining ones sit as thumbnails along its bottom edge.
  const skuImages = isVariantSku && selectedSku ? selectedSku.images : [];
  const activeImageUrl = skuImages[activeImageIndex] || displayImageUrl;
  const thumbnailImages = skuImages
    .map((url, index) => ({ url, index }))
    .filter(({ index }) => index !== activeImageIndex);
  const hasDiscount = !isPricePending && Boolean(displayMrp && displayMrp > displayPrice);
  const discountPercent = hasDiscount
    ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100)
    : 0;

  const validationErrors = collectValidationErrors({
    isVariantSku,
    variantGroups,
    variantSelections,
    selectedSku,
    modifierGroups,
    modifierSelections,
    comboGroups,
    comboPositionState,
    itemUpsellGroups,
    itemUpsellSelections,
  });

  const isDisabled =
    isOutOfStock ||
    quantity < 1 ||
    validationErrors.length > 0 ||
    addToCart.isPending ||
    updateCartLine.isPending;
  const hasConfiguration =
    variantGroups.length > 0 ||
    modifierGroups.length > 0 ||
    sellingAlternativeUnits.length > 0 ||
    comboGroups.length > 0 ||
    itemUpsellGroups.length > 0 ||
    (showIngredients && dedupedIngredients.length > 0);
  const primaryError = validationErrors[0] ?? null;

  const toggleVariant = (groupId, optionId) => {
    if (isVariantSku) {
      if (isSkuOptionBlocked(variantOptionStates?.[groupId]?.[optionId])) return;
      const nextSelections = applyVariantSelection(
        variantGroups,
        variantSkus,
        variantSelections,
        groupId,
        optionId,
      );
      setVariantSelections(nextSelections);
      // A combination with its own modifiers replaces the modifier list, so
      // picks made against the previous list no longer apply.
      const nextSku = findSelectedSku(variantGroups, variantSkus, nextSelections);
      const nextSourceKey = nextSku?.hasOwnModifiers ? `sku:${nextSku.id}` : 'item';
      if (nextSourceKey !== modifierSourceKey) {
        setModifierSelections({});
        setModifierCustomSelections({});
      }
      return;
    }

    const isDeselecting = variantSelections[groupId] === optionId;
    setVariantSelections((current) => ({
      ...current,
      [groupId]: isDeselecting ? undefined : optionId,
    }));

    if (!isDeselecting) {
      const group = variantGroups.find((g) => g.id === groupId);
      const selectedOpt = group?.options?.find((o) => o.id === optionId);
      const variantId = selectedOpt?.variant_id || selectedOpt?.id || optionId;
      if (variantId) {
        fetchRecipeRulesForVariant(variantId);
      }
    }
  };

  const setModifierQuantity = (group, option, nextQty) => {
    setModifierSelections((current) => {
      const groupSelection = { ...(current[group.id] || {}) };
      const maxAllowedQty = getModifierGroupMaxQuantity(group, option, groupSelection);
      const cappedQty =
        maxAllowedQty > 0 ? Math.min(maxAllowedQty, Math.max(0, nextQty)) : Math.max(0, nextQty);
      const parentKey = `${group.id}::${option.id}`;

      if (group.selectionType === 'SINGLE' && cappedQty > 0) {
        const cleared = { [option.id]: cappedQty };
        setModifierCustomSelections((prev) => {
          const next = { ...prev };
          for (const sibling of group.options) {
            if (sibling.id !== option.id) delete next[`${group.id}::${sibling.id}`];
          }
          return next;
        });
        return { ...current, [group.id]: cleared };
      }

      if (cappedQty <= 0) {
        delete groupSelection[option.id];
        setModifierCustomSelections((prev) => {
          if (!prev[parentKey]) return prev;
          const next = { ...prev };
          delete next[parentKey];
          return next;
        });
      } else {
        groupSelection[option.id] = cappedQty;
      }

      return { ...current, [group.id]: groupSelection };
    });
  };

  const toggleModifier = (group, option) => {
    const currentQty = modifierSelections[group.id]?.[option.id] ?? 0;
    const parentKey = `${group.id}::${option.id}`;
    if (hasQuantityMode(group, option)) {
      setModifierQuantity(group, option, currentQty > 0 ? 0 : 1);
      return;
    }

    setModifierSelections((current) => {
      const groupSelection = { ...(current[group.id] || {}) };

      if (group.selectionType === 'SINGLE') {
        if (currentQty > 0) {
          setModifierCustomSelections((prev) => {
            if (!prev[parentKey]) return prev;
            const next = { ...prev };
            delete next[parentKey];
            return next;
          });
          return { ...current, [group.id]: {} };
        }
        setModifierCustomSelections((prev) => {
          const next = { ...prev };
          for (const sibling of group.options) {
            if (sibling.id !== option.id) delete next[`${group.id}::${sibling.id}`];
          }
          return next;
        });
        return {
          ...current,
          [group.id]: {
            [option.id]: 1,
          },
        };
      }

      if (currentQty > 0) {
        delete groupSelection[option.id];
        setModifierCustomSelections((prev) => {
          if (!prev[parentKey]) return prev;
          const next = { ...prev };
          delete next[parentKey];
          return next;
        });
      } else {
        groupSelection[option.id] = 1;
      }
      return { ...current, [group.id]: groupSelection };
    });
  };

  const handleAdd = async (e) => {
    const payload = buildCartLinePayload({
      product,
      initialLine,
      selectedVariants,
      selectedModifiers,
      customModifiers: selectedModifierCustomPayload,
      upsellItems: selectedUpsellPayload,
      upsellSelections,
      comboSelections: comboSelectionsPayload,
      removedIngredients: removedIngredientsPayload,
      serviceItems: selectedPayloadServiceItems,
      alternativeUnit: selectedAltConv,
      isVariantSku,
      selectedSku,
      unitPrice: pricing.unitPrice,
      mrp: displayMrp,
      imageUrl: displayImageUrl,
      quantity,
      lineExtras: pricing.lineExtras,
    });

    try {
      if (initialLine?.lineId) {
        await updateCartLine.mutateAsync({
          ...payload,
          lineId: initialLine.lineId,
        });
        toast.success(`${product.name} updated.`);
      } else {
        flyToCart(e, product.imageUrl);
        await addToCart.mutateAsync(payload);
        // Sequential: each add can create/refresh the active cart server-side.
        try {
          for (const line of buildItemUpsellLines(product, selectedItemUpsells)) {
            await addToCart.mutateAsync(line);
          }
        } catch (error) {
          toast.apiError(error, 'Could not add the selected add-ons.');
        }
        toast.success(`${product.name} added.`);
      }
      onDone?.();
    } catch (error) {
      toast.apiError(error, 'Could not add to cart.');
    }
  };

  // Variant-SKU mode: the selected combination's own description wins.
  const productDescription =
    (isVariantSku && (selectedSku?.shortDescription || selectedSku?.description)) ||
    product.shortDescription ||
    product.description;
  const ctaLabel = isOutOfStock
    ? t('outOfStock', 'Out of stock')
    : primaryError
      ? t('selectOptions', 'Select options')
      : initialLine?.lineId
        ? t('updateCart', 'Update')
        : t('addToCart', 'Add to Cart');

  // Overlaid on the media's bottom edge; on mobile the media is a small
  // avatar, so the same strip renders as its own row under the header.
  const renderThumbs = (modifier = '') =>
    thumbnailImages.length > 0 ? (
      <div className={`po-media-thumbs${modifier}`}>
        {thumbnailImages.map(({ url, index }) => (
          <button
            key={`${index}-${url}`}
            type="button"
            className="po-media-thumb"
            aria-label={`${t('viewImage', 'View image')} ${index + 1}`}
            onClick={() => setActiveImageIndex(index)}
          >
            <img src={url} alt="" loading="lazy" />
          </button>
        ))}
      </div>
    ) : null;

  const renderMedia = (place) => (
    <div className="po-media">
      <Image
        src={activeImageUrl || placeholderImage}
        fallback={placeholderImage}
        alt=""
        className="po-media-img"
        width="100%"
        style={{ objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
        wrapperStyle={{ width: '100%', display: 'block' }}
        preview={{
          src: activeImageUrl || placeholderImage,
          mask: (
            <Space direction="vertical" align="center" size={4}>
              <EyeOutlined className="po-media-mask-icon" />
              <span className="po-media-mask-label">{t('viewFullImage', 'View Image')}</span>
            </Space>
          ),
        }}
      />
      {place === 'mobile' ? null : renderThumbs()}
      {onDone ? (
        <button
          type="button"
          className="po-media-close"
          aria-label={t('close', 'Close')}
          onClick={onDone}
        >
          <CloseOutlined />
        </button>
      ) : null}
    </div>
  );

  const renderIdentity = (place) => (
    <div className={`po-identity-block po-identity-block--${place}`}>
      {renderMedia(place)}
      <div className="po-identity">
        <div className="po-identity-tags">
          <FoodTypeMark value={product.foodTypeSlug ?? product.foodType} />
          {product.categoryName && product.categoryName !== 'Root' ? (
            <span className="po-identity-cat">{product.categoryName}</span>
          ) : null}
          {product.uom ? <span className="po-identity-uom">{product.uom}</span> : null}
        </div>
        <h1 className="po-identity-title">{product.name}</h1>
        {productDescription ? <p className="po-identity-desc">{productDescription}</p> : null}
        {hasDiscount ? (
          <div className="po-identity-savings">
            <span className="po-identity-mrp">
              <Price amount={displayMrp} strike />
            </span>
            <span className="po-identity-off">
              {discountPercent}% {t('off', 'off')}
            </span>
          </div>
        ) : null}
      </div>
      {place === 'mobile' ? renderThumbs(' po-media-thumbs--row') : null}
    </div>
  );

  const renderFooter = (place) => (
    <div className={`product-option-footer po-footer po-footer--${place}`}>
      {primaryError && !isOutOfStock ? (
        <div className="po-footer-note">⚠ {primaryError}</div>
      ) : null}
      <div className={`po-footer-bar ${isDisabled ? 'is-disabled' : ''}`}>
        {!isOutOfStock ? (
          <div className="po-footer-qty">
            <QtyStepper
              value={quantity}
              min={1}
              onChange={setQuantity}
              label={t('quantity', 'Quantity')}
            />
          </div>
        ) : null}
        <span className="po-footer-price">
          <span className="po-footer-price-label">{t('total', 'Total')}</span>
          {isPricePending ? (
            <span className="po-footer-price-pending">
              {t('selectOptionsForPrice', 'Select options')}
            </span>
          ) : (
            <Price amount={lineTotal + itemUpsellTotal} />
          )}
        </span>
        <button type="button" className="po-footer-cta" disabled={isDisabled} onClick={handleAdd}>
          {!isOutOfStock ? <ShoppingCartOutlined /> : null}
          <span>{ctaLabel}</span>
        </button>
      </div>
    </div>
  );

  if (!hasConfiguration) {
    return (
      <div className="product-option-view no-configuration">
        <div className="product-option-main">
          <div className="product-option-scroll-area">{renderIdentity('solo')}</div>
          {renderFooter('solo')}
        </div>
      </div>
    );
  }

  return (
    <div className="product-option-view">
      <aside className="product-option-aside">
        {renderIdentity('desktop')}
        {renderFooter('desktop')}
      </aside>

      <div className="product-option-main">
        <div className="product-option-scroll-area">
          {onBack ? (
            <button type="button" className="po-back" onClick={onBack}>
              <ArrowLeftOutlined /> {t('back', 'Back')}
            </button>
          ) : null}

          {renderIdentity('mobile')}

          {variantGroups.map((group) => {
            // Price cards suit item-level single variants (each shows its own
            // price); in variant-SKU mode the price belongs to the combination,
            // so they render as rows like every other variant group.
            const isCardList = group.id === 'single-variants' && !isVariantSku;
            return (
              <section key={group.id} className="po-section">
                <div className="po-section-head">
                  <h3 className="po-section-title">{group.name}</h3>
                  {/* Variant-SKU mode: no Optional/Required badge on variant groups. */}
                  {isVariantSku ? null : (
                    <GroupBadge required={group.required} limit={0} count={0} pick={0} t={t} />
                  )}
                </div>
                <p className="po-section-sub">
                  {group.subtitle ||
                    (group.required
                      ? t('selectOneOption', 'Select 1 option')
                      : t('optionalPickOne', 'optional · pick one'))}
                </p>

                {isCardList ? (
                  <div className="po-card-list">
                    {group.options.map((option) => {
                      const selected = variantSelections[group.id] === option.id;
                      const skuState = variantOptionStates?.[group.id]?.[option.id];
                      const isBlocked = !selected && isSkuOptionBlocked(skuState);
                      return (
                        <div key={option.id}>
                          <button
                            type="button"
                            aria-pressed={selected}
                            onClick={() => toggleVariant(group.id, option.id)}
                            className={`po-card ${selected ? 'is-selected' : ''}${skuStateClass(skuState)}`}
                            disabled={isBlocked}
                          >
                            <span className="po-card-name">{option.name}</span>
                            {skuState ? null : (
                              <span className="po-card-price">
                                <Price amount={baseUnitPrice + (option.priceDelta || 0)} />
                              </span>
                            )}
                            {skuState?.isOutOfStock ? (
                              <span className="po-option-stock">
                                {t('outOfStock', 'Out of stock')}
                              </span>
                            ) : null}
                            <span className={`po-check-badge ${selected ? 'is-selected' : ''}`} />
                          </button>
                          {selected ? (
                            <>
                              {skuState ? null : (
                                <ServiceItemChildren
                                  group={group}
                                  selectedOption={option}
                                  selections={serviceItemSelections}
                                  onToggle={toggleServiceItem}
                                  priceContext={{ locationId, channelId }}
                                  t={t}
                                />
                              )}
                              <UpsellChildren
                                group={group}
                                selectedOption={option}
                                groupKeyPrefix={group.id}
                                selections={upsellSelections}
                                onToggle={toggleUpsellItem}
                                t={t}
                              />
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="po-row-list">
                    {group.options.map((option) => {
                      const selected = variantSelections[group.id] === option.id;
                      const skuState = variantOptionStates?.[group.id]?.[option.id];
                      const isBlocked = !selected && isSkuOptionBlocked(skuState);
                      return (
                        <div key={option.id}>
                          <button
                            type="button"
                            aria-pressed={selected}
                            onClick={() => toggleVariant(group.id, option.id)}
                            className={`po-row ${selected ? 'is-selected' : ''}${skuStateClass(skuState)}`}
                            disabled={isBlocked}
                          >
                            {isVariantSku ? null : (
                              <FoodTypeMark value={option.foodType ?? product.foodType} />
                            )}
                            <span className="po-row-text">
                              <span className="po-row-name">{option.name}</span>
                              {skuState ? null : (
                                <OptionPriceDelta
                                  amount={option.priceDelta}
                                  className="po-row-price"
                                />
                              )}
                              {skuState?.isOutOfStock ? (
                                <span className="po-option-stock">
                                  {t('outOfStock', 'Out of stock')}
                                </span>
                              ) : null}
                            </span>
                            <span className={`po-radio ${selected ? 'is-on' : ''}`} />
                          </button>
                          {selected ? (
                            <>
                              {skuState ? null : (
                                <ServiceItemChildren
                                  group={group}
                                  selectedOption={option}
                                  selections={serviceItemSelections}
                                  onToggle={toggleServiceItem}
                                  priceContext={{ locationId, channelId }}
                                  t={t}
                                />
                              )}
                              <UpsellChildren
                                group={group}
                                selectedOption={option}
                                groupKeyPrefix={group.id}
                                selections={upsellSelections}
                                onToggle={toggleUpsellItem}
                                t={t}
                              />
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}

          {skuServiceOption?.__hasOwnServiceItems ? (
            <section className="po-section">
              <ServiceItemChildren
                group={SKU_SERVICE_GROUP}
                selectedOption={skuServiceOption}
                selections={serviceItemSelections}
                onToggle={toggleServiceItem}
                priceContext={{ locationId, channelId }}
                t={t}
              />
            </section>
          ) : null}

          {packOptions.length > 0 ? (
            <section className="po-section">
              <div className="po-section-head">
                <h3 className="po-section-title">{t('packSize', 'Pack size')}</h3>
                <GroupBadge required={false} limit={0} count={0} pick={0} t={t} />
              </div>
              <p className="po-section-sub">
                {t('unitSubtitle', 'Changes the selling unit sent with your order.')}
              </p>
              <div className="po-chip-row">
                {packOptions.map((pack) => {
                  const selected = selectedAltUnitKey === pack.key;
                  return (
                    <button
                      key={pack.key}
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        setSelectedAltUnitKey((prev) => (prev === pack.key ? null : pack.key))
                      }
                      className={`po-chip ${selected ? 'is-selected' : ''} ${
                        pack.isBestValue ? 'is-flagged' : ''
                      }`}
                    >
                      {pack.isBestValue ? (
                        <span className="po-chip-flag">{t('bestValue', 'Best value')}</span>
                      ) : null}
                      <span>{pack.label}</span>
                      {isPricePending ? null : (
                        <span className="po-chip-delta">
                          {/* Variant-SKU mode: packs are priced off the selected SKU. */}
                          <Price
                            amount={
                              isVariantSku
                                ? resolveAlternativeUnitPrice(pack.conversion, baseUnitPrice)
                                : pack.price
                            }
                          />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedAltPack ? (
                <ServiceItemChildren
                  group={{ id: 'alternative-unit' }}
                  selectedOption={selectedAltPack.serviceOption}
                  selections={serviceItemSelections}
                  onToggle={toggleServiceItem}
                  priceContext={{ locationId, channelId }}
                  t={t}
                />
              ) : null}
            </section>
          ) : null}

          {modifierGroups.map((group) => {
            const selection = modifierSelections[group.id] || {};
            const currentCount = sumQuantities(selection);
            const maxLimit =
              group.maxModifier > 0
                ? group.maxModifier
                : group.maxSelection > 0
                  ? group.maxSelection
                  : 0;
            const isRequired = Boolean(group.required || group.minSelection > 0);
            const requiredPick = isRequired
              ? group.minSelection > 0
                ? group.minSelection
                : group.minModifier
              : 0;
            const isRadioGroup = group.selectionType === 'SINGLE' && !group.allowQuantity;
            return (
              <section key={group.id} className="po-section">
                <div className="po-section-head">
                  <h3 className="po-section-title">{group.name}</h3>
                  <GroupBadge
                    required={isRequired}
                    limit={maxLimit}
                    count={currentCount}
                    pick={requiredPick}
                    t={t}
                  />
                </div>
                <p className="po-section-sub">{modifierSubtitle(group, t)}</p>

                <div className="po-row-list">
                  {group.options.length === 0 ? (
                    <Text type="secondary" className="po-empty-note">
                      {t('noModifiersAvailable', 'This modifier group has no modifier options')}
                    </Text>
                  ) : null}

                  {group.options.map((option) => {
                    const qty = selection[option.id] ?? 0;
                    const selected = qty > 0;
                    const soldOut = isSoldOutByOnlineInventory(option);
                    const parentKey = `${group.id}::${option.id}`;
                    const customSelection = modifierCustomSelections[parentKey];
                    const selectedCount = distinctCount(selection);
                    const totalQty = sumQuantities(selection);
                    const groupMaxReached = group.maxModifier > 0 && totalQty >= group.maxModifier;
                    const selectionLimitReached =
                      (group.maxSelection > 0 && selectedCount >= group.maxSelection) ||
                      (group.maxModifier > 0 && totalQty >= group.maxModifier);
                    const lockedOut = (selectionLimitReached && !selected) || soldOut;
                    return (
                      <div key={option.id}>
                        {isRadioGroup ? (
                          <button
                            type="button"
                            aria-pressed={selected}
                            disabled={soldOut}
                            onClick={() => !soldOut && toggleModifier(group, option)}
                            className={`po-row ${selected ? 'is-selected' : ''} ${soldOut ? 'is-disabled' : ''}`}
                          >
                            <FoodTypeMark value={option.foodType ?? product.foodType} />
                            <span className="po-row-text">
                              <span className="po-row-name">{option.name}</span>
                              <OptionPriceDelta
                                amount={option.priceDelta}
                                className="po-row-price"
                              />
                            </span>

                            {soldOut ? (
                              <span className="po-out-of-stock">
                                {t('outOfStock', 'Out of stock')}
                              </span>
                            ) : (
                              <span className={`po-radio ${selected ? 'is-on' : ''}`} />
                            )}
                          </button>
                        ) : (
                          <div
                            className={`po-row is-static ${selected ? 'is-selected' : ''} ${
                              lockedOut ? 'is-locked' : ''
                            }${soldOut ? 'is-disabled' : ''}
                            `}
                          >
                            <FoodTypeMark value={option.foodType ?? product.foodType} />
                            <span className="po-row-text">
                              <span className="po-row-name">{option.name}</span>
                              <OptionPriceDelta
                                amount={option.priceDelta}
                                className="po-row-price"
                              />
                            </span>
                            {soldOut ? (
                              <span className="po-out-of-stock">
                                {t('outOfStock', 'Out of stock')}
                              </span>
                            ) : selected ? (
                              <QtyStepper
                                value={qty}
                                min={0}
                                label={option.name}
                                max={getModifierGroupMaxQuantity(group, option, selection)}
                                disableIncrement={groupMaxReached || lockedOut}
                                onChange={(nextQty) => setModifierQuantity(group, option, nextQty)}
                              />
                            ) : (
                              <button
                                type="button"
                                className="po-row-add"
                                disabled={lockedOut}
                                aria-label={`${t('add', 'Add')} ${option.name}`}
                                onClick={() => setModifierQuantity(group, option, 1)}
                              >
                                <PlusOutlined />
                              </button>
                            )}
                          </div>
                        )}
                        {selected && hasModifierCustomOptions(option) ? (
                          <ModifierCustomChoices
                            modifierOption={option}
                            modifierSelection={customSelection || { alternate: null, extras: {} }}
                            onSelectModifierAlternate={(alternate) =>
                              setModifierAlternate(parentKey, alternate)
                            }
                            onSelectModifierExtra={(extraGroup, item) =>
                              toggleModifierExtra(parentKey, extraGroup, item)
                            }
                            t={t}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {comboGroups.map((group) => {
            const selectedCount = getComboGroupSelectedCount(group, comboPositionState);
            return (
              <section key={group.id} className="po-section">
                <div className="po-section-head">
                  <h3 className="po-section-title">{group.name}</h3>
                  <GroupBadge
                    required={group.required}
                    limit={group.maxSelect}
                    count={selectedCount}
                    pick={group.minSelect}
                    t={t}
                  />
                </div>
                <p className="po-section-sub">
                  {t('pick', 'Pick')} {group.minSelect || 1}
                  {group.maxSelect ? `–${group.maxSelect}` : ''}{' '}
                  {t('optionCountSuffix', 'option(s)')}
                </p>

                <div className="po-combo-list">
                  {group.positions.map((position, positionIndex) => {
                    const info = comboPositionInfo[position.id];
                    const selected = info.quantity > 0;
                    const nestedModifierGroups = asArray(info.componentDetail?.modifierGroups);
                    const comboCap = getComboPositionQuantityCap(
                      group,
                      position,
                      comboPositionState,
                    );
                    return (
                      <div
                        key={position.id}
                        className={`po-combo-position ${selected ? 'is-selected' : ''}`}
                      >
                        <div
                          className="po-combo-pos-main"
                          role={!group.isMultiSelect ? 'button' : undefined}
                          tabIndex={!group.isMultiSelect ? 0 : undefined}
                          onClick={
                            !group.isMultiSelect
                              ? () => toggleComboPosition(group, position)
                              : undefined
                          }
                          onKeyDown={
                            !group.isMultiSelect
                              ? (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    toggleComboPosition(group, position);
                                  }
                                }
                              : undefined
                          }
                        >
                          <span className="po-combo-num">{positionIndex + 1}</span>
                          <span className="po-row-text">
                            <span className="po-row-name">
                              {position.name}
                              {info.chip ? (
                                <span className="po-combo-swap"> → {info.chip.name}</span>
                              ) : null}
                            </span>
                            <OptionPriceDelta
                              amount={info.componentPrice}
                              className="po-row-price"
                            />
                          </span>
                          {group.isMultiSelect ? (
                            <QtyStepper
                              value={info.quantity}
                              min={0}
                              label={position.name}
                              max={Number.isFinite(comboCap) ? comboCap : 0}
                              disableIncrement={
                                Number.isFinite(comboCap) && info.quantity >= comboCap
                              }
                              onChange={(nextQty) =>
                                setComboPositionQuantity(group, position, nextQty)
                              }
                            />
                          ) : (
                            <span className={`po-check ${selected ? 'is-on' : ''}`} />
                          )}
                        </div>

                        {position.chips.length > 0 ? (
                          <div className="po-chip-row po-combo-chips">
                            {position.chips.map((chip) => {
                              const isActive = info.swapKey === chip.key;
                              return (
                                <button
                                  key={chip.key}
                                  type="button"
                                  aria-pressed={isActive}
                                  onClick={() => selectComboChip(group, position, chip)}
                                  className={`po-chip ${isActive ? 'is-selected' : ''}`}
                                >
                                  <span>{chip.name}</span>
                                  <OptionPriceDelta
                                    amount={chip.priceDelta}
                                    className="po-chip-delta"
                                  />
                                </button>
                              );
                            })}
                          </div>
                        ) : null}

                        {selected && nestedModifierGroups.length > 0 ? (
                          <div className="po-children">
                            {nestedModifierGroups.map((nestedGroup) => (
                              <div key={nestedGroup.id} className="po-child-block">
                                <div className="po-child-label">
                                  <span className="po-child-label-title">{nestedGroup.name}</span>
                                </div>
                                <div className="po-row-list">
                                  {nestedGroup.options.map((option) => {
                                    const nestedSelection =
                                      comboComponentModifierSelections[info.componentKey]?.[
                                        nestedGroup.id
                                      ] || {};
                                    const nestedQty = nestedSelection[option.id] ?? 0;
                                    const nestedSoldOut = isSoldOutByOnlineInventory(option);
                                    return (
                                      <div
                                        key={option.id}
                                        className={`po-row is-static ${
                                          nestedQty > 0 ? 'is-selected' : ''
                                        }${nestedSoldOut ? 'is-disabled' : ''}`}
                                      >
                                        <FoodTypeMark value={option.foodType ?? product.foodType} />
                                        <span className="po-row-text">
                                          <span className="po-row-name">{option.name}</span>
                                          <OptionPriceDelta
                                            amount={option.priceDelta}
                                            className="po-row-price"
                                          />
                                        </span>
                                        {nestedSoldOut ? (
                                          <span className="po-out-of-stock">
                                            {t('outOfStock', 'Out of stock')}
                                          </span>
                                        ) : nestedQty > 0 ? (
                                          <QtyStepper
                                            value={nestedQty}
                                            min={0}
                                            label={option.name}
                                            max={getModifierGroupMaxQuantity(
                                              nestedGroup,
                                              option,
                                              nestedSelection,
                                            )}
                                            onChange={(nextQty) =>
                                              setComboComponentModifierQuantity(
                                                info.componentKey,
                                                nestedGroup,
                                                option,
                                                nextQty,
                                              )
                                            }
                                          />
                                        ) : (
                                          <button
                                            type="button"
                                            className="po-row-add"
                                            disabled={nestedSoldOut}
                                            aria-label={`${t('add', 'Add')} ${option.name}`}
                                            onClick={() =>
                                              setComboComponentModifierQuantity(
                                                info.componentKey,
                                                nestedGroup,
                                                option,
                                                1,
                                              )
                                            }
                                          >
                                            <PlusOutlined />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {showIngredients && dedupedIngredients.length > 0 ? (
            <section className="po-section">
              <div className="po-section-head">
                <h3 className="po-section-title">{t('removeIngredients', 'Ingredients')}</h3>
                <GroupBadge required={false} limit={0} count={0} pick={0} t={t} />
              </div>
              <p className="po-section-sub">{t('tapToExclude', 'Tap to exclude an ingredient')}</p>
              <div className="po-chip-row">
                {dedupedIngredients.map((ingredient) => {
                  const removed = removedIngredientKeys.has(ingredient.id);
                  return (
                    <button
                      key={ingredient.id}
                      type="button"
                      aria-pressed={removed}
                      onClick={() => toggleIngredient(ingredient.id)}
                      className={`po-ing-chip ${removed ? 'is-removed' : ''}`}
                    >
                      {ingredient.name}
                      {removed ? ` · ${t('removed', 'removed')}` : ''}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {itemUpsellGroups.map((group) => (
            <ItemUpsellSection
              key={group.id}
              group={group}
              selection={itemUpsellSelections[group.id]}
              onChange={setItemUpsellQty}
              t={t}
            />
          ))}
        </div>

        {renderFooter('mobile')}
      </div>
    </div>
  );
}
